import React, { useMemo, useRef, useState } from "react"
import { NodeResult, RunDto } from "@custom-types/modelRun"
import { useGetRunsNodesResultsHistoricQuery } from "@store/services/runService"
import { GraphNodeData } from "@store/services/converter/types"
import {
  Box,
  CircularProgress,
  FormControl,
  FormLabel,
  Grid,
  Menu,
  Slider,
  Typography,
} from "@mui/material"
import { Bar } from "react-chartjs-2"
import * as d3 from "d3"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import { color_tones, lighter_rgba, to_rgba } from "@features/results/Result/const"
import ChartDataLabels from "chartjs-plugin-datalabels"
import DownloadChartImageDialog from "@features/results/DownloadChartImageDialog"
import { Chart } from "chart.js"
import { useLocalStorage } from "usehooks-ts"
import { areResultsOfTheSameUnitFormat, calculateLagShift } from "utils/run"
import { graphViewGroupNodeLabelToSampleNode } from "@features/graph/utils/graphUtils"
import { getNumberWithOrdinal } from "@features/results/const"
import { convertHistoricToRunResults } from "@features/results/Result/runResult.utils"
import { generateChartLabel } from "@utils/chartLabelUtils"

type TornadoChartProps = {
  run1: RunDto | undefined
  run2: RunDto | undefined
  node: GraphNodeData | null
}

type ChartDataType = {
  labels: number[]
  dataSet1Mean: number[]
  dataSet2Mean: number[]
  dataSet1Percentile: number[]
  dataSet2Percentile: number[]
}

export function TornadoChart({ run1, run2, node }: TornadoChartProps) {
  const parsedNodeLabel = node ? graphViewGroupNodeLabelToSampleNode(node.display.label) : ""

  const { isFetching, data: historicResponse } = useGetRunsNodesResultsHistoricQuery(
    {
      runKeys: [run1?.key ?? "", run2?.key ?? ""],
      nodes: [parsedNodeLabel],
    },
    {
      skip: !run1 || !run2,
    },
  )

  const results = useMemo(
    () => convertHistoricToRunResults(historicResponse?.results),
    [historicResponse?.results],
  )

  const runResults = Object.values(results ?? {})
  const resultGetRunFilterNode1: NodeResult<number>[] | undefined = runResults[0]?.[parsedNodeLabel]
  const resultGetRunFilterNode2: NodeResult<number>[] | undefined = runResults[1]?.[parsedNodeLabel]

  const chartRef = useRef<Chart<"bar">>(null)
  const chartContainerRef = useRef<HTMLDivElement | null>(null)

  const [chartOptions, setChartOptions] = useLocalStorage<{
    percentileValue: number
  }>(`tornadoChartOptions`, {
    percentileValue: 70,
  })
  const lagShift = useMemo(
    () => calculateLagShift(historicResponse?.results),
    [historicResponse?.results],
  )

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const onSliderChange = (event: Event, value: number | number[], activeThumb: number) => {
    setChartOptions((prevState) => {
      return {
        ...prevState,
        percentileValue: +value,
      }
    })
  }

  const chartDataset: ChartDataType = useMemo(() => {
    const chartDataResult: ChartDataType = {
      labels: [],
      dataSet1Mean: [],
      dataSet2Mean: [],
      dataSet1Percentile: [],
      dataSet2Percentile: [],
    }
    if (resultGetRunFilterNode1 || resultGetRunFilterNode2) {
      resultGetRunFilterNode1?.forEach((item, index) => {
        if (index < lagShift) return

        chartDataResult.labels.push(item.timestep)

        const sortedValues = item.data.slice().sort(function (a, b) {
          return a - b
        })

        chartDataResult.dataSet1Mean.push(-(d3.mean(sortedValues) || 0))
        chartDataResult.dataSet1Percentile.push(
          -(d3.quantileSorted(sortedValues, chartOptions.percentileValue / 100) || 0),
        )
      })

      chartDataResult.labels.forEach((timestep) => {
        const item = resultGetRunFilterNode2?.find((item) => item.timestep === timestep)
        if (item) {
          const sortedValues = item.data.slice().sort(function (a, b) {
            return a - b
          })

          chartDataResult.dataSet2Mean.push(d3.mean(sortedValues) || 0)
          chartDataResult.dataSet2Percentile.push(
            d3.quantileSorted(sortedValues, chartOptions.percentileValue / 100) || 0,
          )
        } else {
          chartDataResult.dataSet2Mean.push(0)
          chartDataResult.dataSet2Percentile.push(0)
        }
      })
    }

    return chartDataResult
  }, [resultGetRunFilterNode1, resultGetRunFilterNode2, chartOptions, lagShift])

  const data = useMemo(() => {
    return {
      labels: chartDataset.labels,
      datasets: [
        {
          label: generateChartLabel({
            nodeName: node?.display.label ?? "",
            nodeUnit: node?.display["Unit Format"],
            runName: run1?.run_name ?? undefined,
            runVersion: run1?.version ?? undefined,
            numberOfRuns: 2,
            statisticalSuffix: "mean",
          }),
          data: chartDataset.dataSet1Mean,
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          borderColor: to_rgba(lighter_rgba(color_tones[0], 0, 1)),
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          backgroundColor: to_rgba(lighter_rgba(color_tones[0], 0, 1)),
        },
        {
          label: generateChartLabel({
            nodeName: node?.display.label ?? "",
            nodeUnit: node?.display["Unit Format"],
            runName: run1?.run_name ?? undefined,
            runVersion: run1?.version ?? undefined,
            numberOfRuns: 2,
            statisticalSuffix: `${getNumberWithOrdinal(chartOptions.percentileValue)} percentile`,
          }),
          data: chartDataset.dataSet1Percentile,
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          borderColor: to_rgba(lighter_rgba(color_tones[0], 0, 0.5)),
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          backgroundColor: to_rgba(lighter_rgba(color_tones[0], 0, 0.5)),
        },
        {
          label: generateChartLabel({
            nodeName: node?.display.label ?? "",
            nodeUnit: node?.display["Unit Format"],
            runName: run2?.run_name ?? undefined,
            runVersion: run2?.version ?? undefined,
            numberOfRuns: 2,
            statisticalSuffix: "mean",
          }),
          data: chartDataset.dataSet2Mean,
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          borderColor: to_rgba(lighter_rgba(color_tones[1], 0, 1)),
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          backgroundColor: to_rgba(lighter_rgba(color_tones[1], 0, 1)),
        },
        {
          label: generateChartLabel({
            nodeName: node?.display.label ?? "",
            nodeUnit: node?.display["Unit Format"],
            runName: run2?.run_name ?? undefined,
            runVersion: run2?.version ?? undefined,
            numberOfRuns: 2,
            statisticalSuffix: `${getNumberWithOrdinal(chartOptions.percentileValue)} percentile`,
          }),
          data: chartDataset.dataSet2Percentile,
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          borderColor: to_rgba(lighter_rgba(color_tones[1], 0, 0.5)),
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          backgroundColor: to_rgba(lighter_rgba(color_tones[1], 0, 0.5)),
        },
      ],
    }
  }, [chartDataset, chartOptions, node, run1, run2])

  const areTheSameUnitsUsed = areResultsOfTheSameUnitFormat(results)
  if (!areTheSameUnitsUsed) {
    return (
      <Typography>
        Can not render <strong>tornado chart</strong> because selected node(s) are using different
        units.
      </Typography>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  const options: any = {
    indexAxis: "y" as const,
    elements: {
      bar: {
        borderWidth: 2,
      },
    },
    plugins: {
      datalabels: {
        color: "#000",
        display: "auto",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
        anchor: function (d: any) {
          const lbl = d.dataset.label
          return lbl.includes("Percentile") ? (d.datasetIndex % 2 == 0 ? "end" : "start") : "center"
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
        align: function (d: any) {
          const lbl = d.dataset.label
          return lbl.includes("Percentile") ? (d.datasetIndex % 2 == 0 ? "end" : "start") : "center"
        },
        formatter: function (d: number) {
          return Math.abs(+d.toFixed(2))
        },
      },
      tooltip: {
        mode: "index",
        position: "average",
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
          label: function (tooltipItem: any) {
            return [tooltipItem.dataset.label, Math.abs(tooltipItem.raw).toFixed(2)]
          },
        },
      },
    },
    responsive: true,
    scales: {
      y: {
        stacked: true,
      },
    },
  }

  return (
    <Box sx={{ m: 2 }}>
      {isFetching ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={1} alignItems="center">
          <Grid item xs="auto">
            <Box>
              <Box onClick={handleClick} sx={{ height: "48px", lineHeight: "48px" }}>
                {anchorEl ? (
                  <KeyboardArrowUpIcon
                    sx={{
                      verticalAlign: "middle",
                      position: "relative",
                      bottom: "2px",
                    }}
                  />
                ) : (
                  <KeyboardArrowDownIcon
                    sx={{
                      verticalAlign: "middle",
                      position: "relative",
                      bottom: "2px",
                    }}
                  />
                )}
                {"Chart options"}
              </Box>
              <Menu
                id="menu"
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "menu",
                  sx: { width: "250px" },
                }}
              >
                <FormControl>
                  <FormLabel sx={{ ml: 2 }} id="percentile-label">
                    Percentile
                  </FormLabel>
                  <Slider
                    sx={{ ml: 2, width: 220 }}
                    min={51}
                    max={99}
                    defaultValue={70}
                    value={chartOptions.percentileValue}
                    onChange={onSliderChange}
                    valueLabelDisplay="auto"
                  />
                </FormControl>
              </Menu>
            </Box>
          </Grid>
          <Grid item>
            <DownloadChartImageDialog
              buttonProps={{
                disabled: isFetching,
                sx: { ml: 1 },
              }}
              chartRef={chartContainerRef}
              defaultFileName="Mean/Percentile Comparison Run vs. Run"
              runReference={`${run1?.key} vs. ${run2?.key}`}
            />
          </Grid>
          <Grid item xs={12} ref={chartContainerRef}>
            <Typography variant="h6" textAlign="center" sx={{ mb: 1 }}>
              <b>{node?.display.label ?? ""}</b> Mean/Percentile Comparison Run vs. Run
            </Typography>
            <Bar
              options={options}
              plugins={[ChartDataLabels]}
              data={data}
              height={100}
              ref={chartRef}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  )
}
